import { useState, useCallback, ReactElement } from 'react';
import {
  useListingStore,
  useResourceEditor,
  type ValidationRule as FLCValidationRule,
  type EditableField,
} from '@brightmls/nextgen.lae.flc';
import {
  FieldMetadata,
  useMetadata,
} from '../../../../contexts/MetadataContext';
import { useSidePanel } from '../../../../contexts/SidePanelContext';
import { ListingResource } from '../../../../system/field';
import ResourceForm from './ResourceForm';
import { SxProps } from '@mui/material';
import { MemoizedFieldContextProps } from '../../../../pages/edit/components/MemoizedField';

export type ValidationRule = FLCValidationRule;

export interface ResourceField extends EditableField {
  Key: string;
  Label?: string;
  Layout?: string;
  Control?: string;
  Metadata?: Partial<FieldMetadata>;
  passResourceFormContext?: boolean;
  SkipRequiredIfReadOnly?: boolean;
  SkipRequired?: boolean;
  ForceRequired?: boolean;
  ForceNeverDisabled?: boolean;
  onFieldChange?: (value: any, context: MemoizedFieldContextProps) => void;
}

interface ResourceFormConfig {
  fields: ResourceField[];
  resourceType: ListingResource;
  resourceIndex?: number | Function;
  saveAndAddAnother?: boolean;
  internalResourceManagement?: boolean;
  formHeader?: ReactElement;
  formStyles?: SxProps;
}

interface ResourceFormDisplayConfig {
  title: string;
  width?: number;
}

export const useResourceForm = () => {
  const {
    field: getFieldMetadata,
    lookupValueForListingField,
    displayValueForListingField,
  } = useMetadata();
  const { showPanel, hidePanel } = useSidePanel();
  const [isOpen, setIsOpen] = useState(false);

  const openResourceForm = useCallback(
    (
      formConfig: ResourceFormConfig,
      displayConfig: ResourceFormDisplayConfig,
      saveHook?: (resource: any, isCreate: boolean) => any | false,
      initialValues?: any,
    ) => {
      const isAddition = formConfig.resourceIndex === undefined;

      // Add verification of resource existence
      if (!isAddition) {
        const currentResources = useListingStore
          .getState()
          .getResource(formConfig.resourceType);
        if (
          !currentResources ||
          (Array.isArray(currentResources) && currentResources.length === 0)
        ) {
          console.warn('Resources not yet available in listing context');
          return;
        }
      }

      const handleSave = async (values: any, saveAndAddAnother: boolean) => {
        let processedValues = values;

        // Call custom save hook if provided
        if (saveHook) {
          processedValues = await Promise.resolve(saveHook(values, isAddition));
          if (processedValues === false) {
            // Save hook returned false, just close without saving
            hidePanel();
            setIsOpen(false);
            return;
          }
        }

        // Save to store unless internal management is enabled
        if (!formConfig.internalResourceManagement) {
          const store = useListingStore.getState();

          if (formConfig.resourceIndex !== undefined) {
            const index =
              typeof formConfig.resourceIndex === 'function'
                ? formConfig.resourceIndex()
                : formConfig.resourceIndex;
            store.updateResource(
              formConfig.resourceType,
              index,
              processedValues,
            );
          } else {
            store.addResource(formConfig.resourceType, processedValues);
          }
        }

        if (saveAndAddAnother) {
          // Reset form with fresh instance
          showPanel(
            displayConfig.title,
            <ResourceForm
              key={Date.now()}
              saveAndAddAnother={
                (isAddition && formConfig.saveAndAddAnother) || false
              }
              formHeader={formConfig.formHeader}
              formStyles={formConfig.formStyles}
              resourceType={formConfig.resourceType}
              fields={formConfig.fields}
              initialValues={undefined}
              resourceIndex={undefined}
              onSave={handleSave}
              onCancel={() => {
                hidePanel();
                setIsOpen(false);
              }}
              getBaseFieldMetadata={getFieldMetadata}
              lookupValueForListingField={lookupValueForListingField}
              displayValueForListingField={displayValueForListingField}
              isAddition={true}
            />,
            undefined,
            displayConfig.width,
          );
        } else {
          hidePanel();
          setIsOpen(false);
        }
      };

      setIsOpen(true);
      showPanel(
        displayConfig.title,
        <ResourceForm
          formHeader={formConfig.formHeader}
          formStyles={formConfig.formStyles}
          saveAndAddAnother={
            (isAddition && formConfig.saveAndAddAnother) || false
          }
          resourceType={formConfig.resourceType}
          fields={formConfig.fields}
          initialValues={initialValues}
          resourceIndex={formConfig.resourceIndex}
          onSave={handleSave}
          onCancel={() => {
            hidePanel();
            setIsOpen(false);
          }}
          getBaseFieldMetadata={getFieldMetadata}
          lookupValueForListingField={lookupValueForListingField}
          displayValueForListingField={displayValueForListingField}
          saveHook={saveHook}
          isAddition={isAddition}
        />,
        undefined,
        displayConfig.width,
      );
    },
    [
      showPanel,
      getFieldMetadata,
      lookupValueForListingField,
      displayValueForListingField,
      hidePanel,
    ],
  );

  return {
    openResourceForm,
    isOpen,
  };
};
